import { getCognigyChatStyling } from 'api/contentful-api';
import { useEffect, useState } from 'react';
import { ACCESS_TOKEN } from 'shared/constants/cookies';
import { cookies } from 'shared/utils/storage/cookies';
import { CIAMToken } from 'utils/auth';
import { getStringValuesFromLocalStorage } from 'utils/storage';
import { isMobileApp, isNetcrackerApp } from 'utils/user-agent';
import {
  CHAT_SESSION_ID_KEY,
  CHAT_USER_ID_KEY,
  TriggerType,
  checkUrlParamsForCognigyIds,
  findLocalStorageKey,
  generateSessionId,
  getChatSessionId,
  getChatState,
  getChatUserId,
  isTimestampWithin72Hours,
  triggerTypes,
  updateLocalStorageChatSessionId,
  updateLocalStorageChatState,
  updateLocalStorageChatUserId,
} from './cognigy/cognigyChatUtils';

declare global {
  interface Window {
    cognigyWebchat: any;
    initWebchat: any;
    initWebchatData: boolean;
  }
}

type CognigyEventPayload = {
  text?: string;
  data: {
    nd_action: string;
    reason: string;
  };
};

export type CognigyEventType = {
  type: string;
  payload?: CognigyEventPayload;
};

const COGNIGYSCRIPT = 'https://github.com/Cognigy/Webchat/releases/download/v3.4.0/webchat.js';
const COGNIGY_URL =
  process.env.NODE_ENV === 'production'
    ? 'https://endpoint-yousee.cognigy.cloud/e360170a4dd67fee63a97b2ea858f5bc127edfae0c003585122201f7568de847'
    : 'https://endpoint-yousee.cognigy.cloud/bdc838e384e944b5794740dba84ae7aad3f495366f9f5f106e57403ff40f3e01';

const getCognigyEndpoint = () => {
  return COGNIGY_URL;
};

const initPayload = (
  userId: string | undefined,
  token: CIAMToken | undefined,
  sessionId: string | undefined,
  triggerType?: TriggerType,
) => {
  window.initWebchatData = true;
  return {
    customer: {
      customerId: userId,
    },
    accessToken: token,
    sessionId,
    datetime: new Date().toISOString(),
    currentUrl: window.location.href,
    platform: 'web',
    trigger: {
      type: triggerType ? triggerType : 'Selfcare',
      payload: {},
    },
  };
};

export const useCognigyScript = () => {
  const [isScriptLoaded, setIsScriptLoaded] = useState<boolean>(false);
  const [isChatInitialized, setIsChatInitialized] = useState<boolean>(false);
  const [webChat, setWebChat] = useState<any>(null);
  const [accessToken, setAccessToken] = useState<CIAMToken | undefined>(undefined);
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const { userIdFromUrl, sessionIdFromUrl } = checkUrlParamsForCognigyIds();
  const [triggerType] = useState<TriggerType>(
    userIdFromUrl || sessionIdFromUrl ? triggerTypes.VoiceNudge : triggerTypes.Selfcare,
  );

  const chatStateFromLocalStorage = getChatState();
  const isChatStateAvailable = chatStateFromLocalStorage !== null;
  const isChatOpen = chatStateFromLocalStorage === 'true';

  useEffect(() => {
    if (isNetcrackerApp || isMobileApp) {
      return;
    }

    const script = document.querySelector(`script[src='${COGNIGYSCRIPT}']`) as HTMLElement;
    const handleScriptLoad = () => setIsScriptLoaded(true);
    if (!script) {
      const newScript = document.createElement('script');
      newScript.src = COGNIGYSCRIPT;
      newScript.async = true;
      document.head.appendChild(newScript);
      newScript.addEventListener('load', handleScriptLoad);
    } else {
      script.addEventListener('load', handleScriptLoad);
    }
    const fetchStyleUrl = async () => {
      const cognigyCss = await getCognigyChatStyling();
      const link = document.querySelector(`link[href='${cognigyCss}']`);
      if (!link) {
        const cognigyCssTag = document.createElement('link');
        cognigyCssTag.rel = 'stylesheet';
        cognigyCssTag.type = 'text/css';
        cognigyCssTag.href = cognigyCss;
        document.head.appendChild(cognigyCssTag);
      }
    };
    fetchStyleUrl();

    const token = cookies.get(ACCESS_TOKEN, true);
    if (token) {
      setAccessToken(token);
    }

    const tempUserId = getChatUserId();
    const tempSessionId = getChatSessionId();
    if (tempUserId && tempSessionId) {
      const knownParts = ['webchat-client', tempUserId, tempSessionId];
      const matchingKey = findLocalStorageKey(knownParts);

      if (matchingKey) {
        const value = localStorage.getItem(matchingKey);
        try {
          const parsedValue = JSON.parse(value || '');
          const userMessages = parsedValue.messages.filter(
            (message: any) => message.source === 'user',
          );
          const lastUerMessage = userMessages[userMessages.length - 1];
          const timestamp = lastUerMessage.timestamp;
          if (isTimestampWithin72Hours(timestamp)) {
            setUserId(tempUserId);
            setSessionId(tempSessionId);
          } else {
            setUserId(tempUserId);
            const newChatSessionId = generateSessionId();
            setSessionId(newChatSessionId);
            updateLocalStorageChatSessionId(newChatSessionId);
            localStorage.removeItem(matchingKey);
          }
        } catch (error) {
          // Key exists. Upnarsable Value
          setUserId(tempUserId);
          setSessionId(tempSessionId);
        }
      } else {
        // No matching key found
        setUserId(tempUserId);
        setSessionId(tempSessionId);
      }
    }
    return () => {
      // Cleanup: Remove the event listener
      if (script) {
        script.removeEventListener('load', handleScriptLoad);
      }
    };
  }, []);

  const cognigyActions = (event: CognigyEventType) => {
    if (event.type === 'webchat/open') {
      updateLocalStorageChatState(true);
    }
    if (event.type === 'webchat/close') {
      updateLocalStorageChatState(false);
    }
    if (event.type === 'webchat/incoming-message' && event.payload?.data?.nd_action) {
      const action = event.payload.data.nd_action;
      if (action === 'close_widget') {
        updateLocalStorageChatState(false);
      }
    }
    if (event.type === 'webchat/incoming-message' && event.payload?.data?.nd_action) {
      const action = event.payload.data.nd_action;
      if (action === 'close_widget') {
        updateLocalStorageChatState(false);
        window.cognigyWebchat.close();
      }
    }
  };

  const sendPayload = (webchat: any, payloadTriggerType?: TriggerType) => {
    const ndChatSessionId = getStringValuesFromLocalStorage(CHAT_SESSION_ID_KEY);
    const ndChatUserId = getStringValuesFromLocalStorage(CHAT_USER_ID_KEY);

    const hasUrlParams: boolean = !!userIdFromUrl && !!sessionIdFromUrl;
    const hasLocalStorageValues: boolean = !!ndChatUserId && !!ndChatSessionId;
    const areUrlParamsSameAsInLocalStorage: boolean =
      userIdFromUrl === ndChatUserId && sessionIdFromUrl === ndChatSessionId;

    const isSendPayloadCondition: boolean =
      (hasUrlParams && !areUrlParamsSameAsInLocalStorage) ||
      (!hasLocalStorageValues && !hasUrlParams);

    if (isSendPayloadCondition) {
      if (sessionId) {
        updateLocalStorageChatSessionId(sessionId);
      }
      if (userId) {
        updateLocalStorageChatUserId(userId);
      }
      webchat.sendMessage('', initPayload(userId, accessToken, sessionId, payloadTriggerType));
    }
  };

  const initWebChat = (defaultOpen = false, passedTriggerType?: TriggerType) => {
    if (window && window.cognigyWebchat === undefined) {
      window
        .initWebchat(getCognigyEndpoint(), {
          userId: userId,
          sessionId: sessionId,
        })
        .then((webchat: any) => {
          window.cognigyWebchat = webchat;
          sendPayload(webchat, passedTriggerType ? passedTriggerType : triggerType);
          webchat.registerAnalyticsService(cognigyActions);
          if (defaultOpen) {
            if (sessionIdFromUrl && userIdFromUrl) {
              webchat.open();
              webchat.startConversation();
            } else {
              webchat.open();
              if (isChatStateAvailable) {
                webchat.startConversation();
              }
            }
          }
          setWebChat(webchat);
          setIsChatInitialized(true);
        });
    }
  };

  const initializeCognigyChat = (defaultOpen = false, triggerType?: TriggerType) => {
    if (isChatInitialized) {
      if (webChat && webChat.client.socketOptions.userId === userIdFromUrl) {
        webChat.startConversation();
        webChat.open();
      } else if (defaultOpen) {
        webChat.open();
      }
    } else {
      initWebChat(defaultOpen, triggerType);
    }
  };

  const checkWebchatInstance = () => {
    if (webChat.client.socketOptions.userId === userIdFromUrl) {
      webChat.startConversation();
      webChat.toggle();
    } else {
      initializeCognigyChat(true);
    }
  };

  if (isScriptLoaded && accessToken) {
    if (userIdFromUrl && sessionIdFromUrl) {
      if (isChatInitialized) {
        checkWebchatInstance();
      } else {
        initializeCognigyChat(true);
      }
    } else if (isChatStateAvailable) {
      initializeCognigyChat(isChatOpen);
    } else {
      initializeCognigyChat();
    }
  }
};
